import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { noWhitespaceValidator } from 'src/app/common-components/validators/no-whitespace.validator';
import { getAddLocationCallState } from 'src/app/pages/add-location/state/add-location.selectors';
import { LocationDetailFormValue } from 'src/app/pages/location-details/models/location-detail-form';
import { areLocationsEqual } from '../models/account-location-view.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-add-account-locations',
    templateUrl: './add-account-locations.component.html',
    standalone: false
})
export class AddAccountLocationComponent implements FormComponent, OnDestroy {
   callState$: Observable<CallState>;

   form: UntypedFormGroup;

   location: LocationDetailFormValue;

   locationsToSave: LocationDetailFormValue[] = [];

   newLocation: LocationDetailFormValue = {
      locationId: 0,
      account: null,
      name: null,
      city: null,
      comment: null,
      country: null,
      createDate: null,
      createUser: null,
      directionalPostfix: null,
      directionalPrefix: null,
      houseNumber: null,
      mostRecentE911ValidationResponse: null,
      postalCode: null,
      isE911Validated: false,
      state: null,
      streetName: null,
      streetNameSuffix: null,
      timeZone: null,
      updateDate: null,
      updateUser: null,
   };

   existingLocations: LocationDetailFormValue[];

   isEdit: boolean = false;

   private destroyed$ = new Subject<void>();

   private locationRegexPattern = '^([A-Za-z0-9]+(?:[ ][A-Za-z0-9]+)*)$';

   constructor(
      public dialogRef: MatDialogRef<AddAccountLocationComponent>,
      protected store: Store,
      @Inject(MAT_DIALOG_DATA) public data,
      private fb: UntypedFormBuilder
   ) {
      this.callState$ = this.store.pipe(select(getAddLocationCallState));
      this.location = {
         ...data.location,
      };
      if (this.location.houseNumber != null) {
         this.existingLocations = data.existingLocations.filter((loc) => !areLocationsEqual(loc, data.location));
         this.isEdit = true;
      } else {
         this.existingLocations = data.existingLocations;
      }
      this.form = this.fb.group({
         name: fb.control(this.location.name, [Validators.maxLength(255), containsCharactersValidator()]),
         houseNumber: fb.control(this.location.houseNumber),
         directionalPrefix: fb.control(this.location.directionalPrefix),
         streetName: fb.control(this.location.streetName, [noWhitespaceValidator()]),
         streetNameSuffix: fb.control(this.location.streetNameSuffix),
         directionalPostfix: fb.control(this.location.directionalPostfix),
         timeZone: fb.control(this.location.timeZone),
         city: fb.control(this.location.city),
         state: fb.control(this.location.state, [Validators.pattern('^[A-Z]{2}$'), noWhitespaceValidator()]),
         postalCode: fb.control(this.location.postalCode, [noWhitespaceValidator()]),
         country: fb.control(this.location.country),
         comment: fb.control(this.location.comment, Validators.maxLength(50)),
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }
   onCancel() {
      this.dialogRef.close(this.locationsToSave);
   }

   onSubmit(submitEvent: { location: LocationDetailFormValue; continuous?: boolean }) {
      if (this.form.valid) {
         this.locationsToSave.push(submitEvent.location);
         this.existingLocations.push(submitEvent.location);
         if (!submitEvent.continuous) {
            this.dialogRef.close(this.existingLocations);
         } else {
            this.location = this.newLocation;
            this.resetForm();
         }
      }
   }

   private resetForm() {
      this.form.reset();
      this.form.get('location')?.setValue(null);
   }
}
